// TelefonMobilHinweis.tsx — gelber Hinweis unter „Telefon"/„Mobil", wenn eine
// Nummer im falschen Feld steht, mit Mini-Knopf zum Tauschen/Verschieben
// (User 01.10.2026: Mobilnummer unter „Telefon", Festnetz unter „Mobil").
//
// Die Erkennung ist rein (`telefonMobilHinweis` in @kmvs/forms-core,
// telefon.ts); hier steht nur die Anzeige und die Verdrahtung mit den
// normalen Feld-Settern des Formulars — damit Dirty-Tracking, Speichern und
// die ids der Kommunikationszeilen (commDbIds je Feld) unverändert greifen.
//
// Gesperrte Akte (`<fieldset disabled>`): Hinweis samt Knopf ausgeblendet —
// per CSS (contactRedesign.css, `.nk-telmobil-hinweis`), wie `SperrWert`
// ohne Context oder Prop-Kette.
//
// Ausrichtung: Die Spalten „Privat" und „Geschäftlich" stehen nebeneinander.
// Hat nur EINE Spalte unter einem Feld einen Hinweis, bekommt die andere an
// derselben Stelle einen unsichtbaren Platzhalter mit demselben Text — so
// bleiben „Mobil" und „E-Mail" beider Spalten auf einer Linie, auch wenn der
// Text umbricht. Bei einspaltigem Layout (≤600px) entfällt der Platzhalter.

import * as React from "react";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  telefonMobilAnwenden,
  telefonMobilHinweis,
  type TelefonMobilHinweis,
} from "@kmvs/forms-core";
import { Link } from "react-router";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { doppeltText, type KontaktWeg } from "./doppelteWege";
import type { AndererKontakt } from "./useNummernAndererKontakte";
import "../../redesign/contactRedesign.css";

export { telefonMobilHinweis };
export type { TelefonMobilHinweis };

const useStyles = makeStyles({
  hinweis: {
    justifySelf: "start",
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "4px",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "3px 4px 3px 8px",
    borderRadius: "6px",
    border: `1px solid ${REFINED.goldLine}`,
    backgroundColor: REFINED.goldBg,
    color: REFINED.goldInk,
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  text: { display: "inline-flex", alignItems: "center", columnGap: "6px" },
  knopf: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    padding: "1px 8px",
    borderRadius: "5px",
    border: `1px solid ${REFINED.goldLine}`,
    backgroundColor: REFINED.white,
    color: REFINED.goldInk,
    fontFamily: REFINED.fontUI,
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 1.5,
    cursor: "pointer",
    ":hover": { backgroundColor: "#fff9ea", borderTopColor: REFINED.gold, borderRightColor: REFINED.gold, borderBottomColor: REFINED.gold, borderLeftColor: REFINED.gold },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.gold, outlineOffset: "1px" },
  },
  platz: { visibility: "hidden" },
});

const WarnIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M8 2.2 14.3 13.3H1.7L8 2.2Z" strokeLinejoin="round" />
    <path d="M8 6.6v3" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const TauschIcon: React.FC = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 2.5v11M2.5 11 5 13.5 7.5 11M11 13.5v-11M8.5 5 11 2.5 13.5 5" />
  </svg>
);

/** Text des Hinweises. */
export const hinweisText = (h: TelefonMobilHinweis): string =>
  h.art === "mobil" ? "Das ist eine Mobilnummer." : "Das ist eine Festnetznummer.";

/** Beschriftung des Mini-Knopfs (oder `undefined` ohne Aktion). */
export const knopfText = (h: TelefonMobilHinweis): string | undefined =>
  h.aktion === "tauschen"
    ? "Tauschen"
    : h.aktion === "verschieben"
      ? h.feld === "telefon"
        ? "Nach Mobil verschieben"
        : "Nach Telefon verschieben"
      : undefined;

/**
 * Der Hinweis unter EINEM Feld (`feld`). Zeigt den eigenen Hinweis der Spalte,
 * wenn er zu diesem Feld gehört; sonst ggf. den unsichtbaren Platzhalter für
 * den Hinweis der Nachbarspalte an derselben Stelle; sonst nichts.
 */
export function TelefonMobilHinweisZeile({
  feld,
  hinweis,
  nachbar,
  onAktion,
}: {
  feld: "telefon" | "mobil";
  hinweis: TelefonMobilHinweis | null;
  /** Hinweis der Nachbarspalte — nur für den Platzhalter (Ausrichtung). */
  nachbar?: TelefonMobilHinweis | null;
  onAktion: () => void;
}) {
  const s = useStyles();
  const eigen = hinweis?.feld === feld ? hinweis : null;
  const fremd = !eigen && nachbar?.feld === feld ? nachbar : null;
  const h = eigen ?? fremd;
  if (!h) return null;
  const knopf = knopfText(h);
  if (fremd) {
    return (
      <div className={mergeClasses("nk-telmobil-hinweis platz", s.hinweis, s.platz)} aria-hidden="true">
        <span className={s.text}>
          <WarnIcon />
          {hinweisText(h)}
        </span>
        {knopf && (
          <span className={s.knopf}>
            <TauschIcon />
            {knopf}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className={mergeClasses("nk-telmobil-hinweis", s.hinweis)} role="note">
      <span className={s.text}>
        <WarnIcon />
        {hinweisText(h)}
      </span>
      {knopf && (
        <button type="button" className={s.knopf} onClick={onAktion}>
          <TauschIcon />
          {knopf}
        </button>
      )}
    </div>
  );
}

/**
 * Ein Hinweis unter einem Kommunikationsfeld — „Das ist eine Mobilnummer.",
 * „Diese Nummer steht schon unter …", „Diese Nummer gehört schon zu …".
 * Gleiche Optik wie {@link TelefonMobilHinweisZeile}.
 */
export interface KommHinweis {
  /** Stabil je Hinweisart (React-key). */
  id: string;
  text: React.ReactNode;
  /** Klartext für den unsichtbaren Platzhalter der Nachbarspalte. */
  platzText?: string;
  knopf?: string;
  onKnopf?: () => void;
}

/** {@link TelefonMobilHinweis} als {@link KommHinweis} für das Feld `feld` (sonst `null`). */
export function telefonMobilKommHinweis(
  feld: "telefon" | "mobil",
  hinweis: TelefonMobilHinweis | null,
  onAktion: () => void,
): KommHinweis | null {
  if (hinweis?.feld !== feld) return null;
  const knopf = knopfText(hinweis);
  return { id: "telmobil", text: hinweisText(hinweis), platzText: hinweisText(hinweis), knopf, onKnopf: knopf ? onAktion : undefined };
}

/** „Diese Nummer steht schon unter Telefon (privat)." (`vorbild` = der andere Weg). */
export function doppeltKommHinweis(typ: string, vorbild: KontaktWeg | null | undefined): KommHinweis | null {
  if (!vorbild) return null;
  const text = doppeltText(typ, vorbild);
  return { id: "doppelt", text, platzText: text };
}

/** „Diese Nummer gehört schon zu <Kontakt>." mit Link auf dessen Akte (neuer Tab). */
export function andererKontaktKommHinweis(k: AndererKontakt | null | undefined): KommHinweis | null {
  if (!k) return null;
  return {
    id: "anderer",
    text: (
      <>
        Diese Nummer gehört schon zu{" "}
        <Link to={`/contacts/${k.contactId}`} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
          {k.displayName}
        </Link>
        .
      </>
    ),
    platzText: `Diese Nummer gehört schon zu ${k.displayName}.`,
  };
}

/**
 * Mehrere Hinweise untereinander unter EINEM Feld. `nachbar`: die Hinweise
 * desselben Felds in der Nachbarspalte — wo die eigene Spalte an einer
 * Position keinen hat, steht ein unsichtbarer Platzhalter (Ausrichtung, wie
 * bei {@link TelefonMobilHinweisZeile}).
 */
export function KommHinweise({
  eigen,
  nachbar,
}: {
  eigen: ReadonlyArray<KommHinweis | null | undefined | false>;
  nachbar?: ReadonlyArray<KommHinweis | null | undefined | false>;
}) {
  const s = useStyles();
  const e = eigen.filter(Boolean) as KommHinweis[];
  const n = (nachbar ?? []).filter(Boolean) as KommHinweis[];
  const anzahl = Math.max(e.length, n.length);
  if (!anzahl) return null;
  const zeilen: React.ReactNode[] = [];
  for (let i = 0; i < anzahl; i++) {
    const h = e[i];
    if (h) {
      zeilen.push(
        <div key={h.id} className={mergeClasses("nk-telmobil-hinweis", s.hinweis)} role="note">
          <span className={s.text}>
            <WarnIcon />
            <span>{h.text}</span>
          </span>
          {h.knopf && h.onKnopf && (
            <button type="button" className={s.knopf} onClick={h.onKnopf}>
              <TauschIcon />
              {h.knopf}
            </button>
          )}
        </div>,
      );
      continue;
    }
    const p = n[i];
    zeilen.push(
      <div key={`platz-${p.id}`} className={mergeClasses("nk-telmobil-hinweis platz", s.hinweis, s.platz)} aria-hidden="true">
        <span className={s.text}>
          <WarnIcon />
          <span>{p.platzText ?? (typeof p.text === "string" ? p.text : "")}</span>
        </span>
        {p.knopf && (
          <span className={s.knopf}>
            <TauschIcon />
            {p.knopf}
          </span>
        )}
      </div>,
    );
  }
  return <>{zeilen}</>;
}

/**
 * Hinweis + Aktion für ein Paar Telefon/Mobil, verdrahtet mit den Feld-Settern
 * des Formulars. `erreichbarkeit` (optional) wandert mit der Nummer mit — die
 * Zeiten gehören zur Nummer, nicht zum Feld.
 */
export function useTelefonMobilPaar({
  telefon,
  mobil,
  setTelefon,
  setMobil,
  erreichbarkeitTauschen,
}: {
  telefon: string;
  mobil: string;
  setTelefon: (v: string) => void;
  setMobil: (v: string) => void;
  /** Tauscht/verschiebt die Erreichbarkeit beider Felder (gleiche Richtung wie die Werte). */
  erreichbarkeitTauschen?: (aktion: "tauschen" | "nachMobil" | "nachTelefon") => void;
}) {
  const hinweis = React.useMemo(() => telefonMobilHinweis(telefon, mobil), [telefon, mobil]);
  const ausfuehren = React.useCallback(() => {
    if (!hinweis?.aktion) return;
    const neu = telefonMobilAnwenden(hinweis, { telefon, mobil });
    if (neu.telefon !== telefon) setTelefon(neu.telefon);
    if (neu.mobil !== mobil) setMobil(neu.mobil);
    erreichbarkeitTauschen?.(
      hinweis.aktion === "tauschen" ? "tauschen" : hinweis.feld === "telefon" ? "nachMobil" : "nachTelefon",
    );
  }, [hinweis, telefon, mobil, setTelefon, setMobil, erreichbarkeitTauschen]);
  return { hinweis, ausfuehren };
}

/**
 * Erreichbarkeiten (Feld → Zeiten) passend zur Aktion umhängen. Rein, damit
 * die Formulare es mit ihrem eigenen State-Setter verwenden können.
 */
export function erreichbarkeitUmhaengen(
  alt: Record<string, string>,
  telefonKey: string,
  mobilKey: string,
  aktion: "tauschen" | "nachMobil" | "nachTelefon",
): Record<string, string> {
  const t = alt[telefonKey] ?? "";
  const m = alt[mobilKey] ?? "";
  const next = { ...alt };
  const setze = (k: string, v: string) => {
    if (v) next[k] = v;
    else delete next[k];
  };
  if (aktion === "tauschen") {
    setze(telefonKey, m);
    setze(mobilKey, t);
  } else if (aktion === "nachMobil") {
    setze(mobilKey, t);
    setze(telefonKey, "");
  } else {
    setze(telefonKey, m);
    setze(mobilKey, "");
  }
  return next;
}
